import { useState, useEffect } from "react";
import { format, isToday, isBefore, parseISO } from "date-fns";
import { BsCalendar, BsDownload, BsSearch } from "react-icons/bs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import api from "../../../api";

export default function AttendanceList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [date, setDate] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_BASE_URL}attendance/student`
        );
        setRecords(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, []);

  const getStatusClass = (record) => {
    const recordDate = new Date(record.date);
    const hasClockIn = record.clockInTime;
    const hasClockOut = record.clockOutTime;

    if (isToday(recordDate) && hasClockIn && !hasClockOut) {
      return "text-yellow-600";
    } else if (isBefore(recordDate, new Date()) && hasClockIn && hasClockOut) {
      return "text-green-600";
    } else if (isBefore(recordDate, new Date()) && hasClockIn && !hasClockOut) {
      return "text-red-600";
    }
    return "text-gray-500";
  };

  const getStatusText = (record) => {
    const recordDate = parseISO(record.date);
    const hasClockIn = record.clockInTime;
    const hasClockOut = record.clockOutTime;

    if (isToday(recordDate) && hasClockIn && !hasClockOut) {
      return "In Progress";
    } else if (isBefore(recordDate, new Date()) && hasClockIn && hasClockOut) {
      return "Present";
    } else if (isBefore(recordDate, new Date()) && hasClockIn && !hasClockOut) {
      return "Absent";
    }
    return "Unknown";
  };

  const formatHoursAndMinutes = (decimalHours) => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const calculateTotalHours = () => {
    return filteredRecords.reduce((total, record) => {
      const clockIn = new Date(record.clockInTime);
      const clockOut = new Date(record.clockOutTime);
      if (!isNaN(clockIn) && !isNaN(clockOut)) {
        const diffInMs = clockOut - clockIn;
        return total + diffInMs / 1000 / 60 / 60;
      }
      return total;
    }, 0);
  };

  const filteredRecords = records.filter((record) => {
    const dateValid = record.date && !isNaN(new Date(record.date));
    const formattedDate = dateValid
      ? format(new Date(record.date), "MMM d, yyyy")
      : "";
    const matchesSearch = formattedDate
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      getStatusText(record).toLowerCase() === statusFilter.toLowerCase();
    const matchesDate =
      !date ||
      (dateValid &&
        format(new Date(record.date), "yyyy-MM-dd") ===
          format(date, "yyyy-MM-dd"));
    return matchesSearch && matchesStatus && matchesDate;
  });

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Attendance History", 14, 10);
    autoTable(doc, {
      head: [["Date", "Time In", "Time Out", "Status", "Total Hours"]],
      body: filteredRecords.map((rec) => [
        rec.date && !isNaN(new Date(rec.date))
          ? format(new Date(rec.date), "MMM d, yyyy")
          : "-",
        rec.clockInTime && !isNaN(new Date(rec.clockInTime))
          ? format(new Date(rec.clockInTime), "hh:mm a")
          : "-",
        rec.clockOutTime && !isNaN(new Date(rec.clockOutTime))
          ? format(new Date(rec.clockOutTime), "hh:mm a")
          : "-",
        getStatusText(rec),
        rec.clockInTime && rec.clockOutTime
          ? formatHoursAndMinutes(
              (new Date(rec.clockOutTime) - new Date(rec.clockInTime)) /
                1000 /
                60 /
                60
            )
          : "-",
      ]),
    });
    doc.text(
      `Total Hours: ${formatHoursAndMinutes(calculateTotalHours())}`,
      14,
      doc.autoTable.previous.finalY + 10
    );
    doc.save("attendance.pdf");
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredRecords.map((rec) => ({
        Date:
          rec.date && !isNaN(new Date(rec.date))
            ? format(new Date(rec.date), "MMM d, yyyy")
            : "-",
        "Time In":
          rec.clockInTime && !isNaN(new Date(rec.clockInTime))
            ? format(new Date(rec.clockInTime), "hh:mm a")
            : "-",
        "Time Out":
          rec.clockOutTime && !isNaN(new Date(rec.clockOutTime))
            ? format(new Date(rec.clockOutTime), "hh:mm a")
            : "-",
        Status: getStatusText(rec),
        "Total Hours":
          rec.clockInTime && rec.clockOutTime
            ? formatHoursAndMinutes(
                (new Date(rec.clockOutTime) - new Date(rec.clockInTime)) /
                  1000 /
                  60 /
                  60
              )
            : "-",
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, "attendance.xlsx");
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setDate(null);
  };

  return (
    <div className="p-6 bg-white shadow-lg rounded-lg mt-4">
      <h2 className="text-xl font-semibold">Attendance History</h2>
      <p className="text-gray-600 mb-4">View and filter your attendance records</p>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative w-full md:w-1/3">
          <BsSearch className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search by date..."
            className="pl-10 p-2 w-full border rounded"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          className="p-2 border rounded w-full md:w-1/4"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="present">Present</option>
          <option value="absent">Absent</option>
          <option value="in progress">In Progress</option>
        </select>

        <button className="p-2 border rounded w-full md:w-1/4 flex items-center justify-center gap-2">
          <BsCalendar className="w-4 h-4" />
          {date ? format(date, "PPP") : "Pick a date"}
        </button>
      </div>

      <div className="flex justify-between gap-2 mb-4">
        <button className="p-2 border rounded flex items-center gap-2" onClick={clearFilters}>
          Clear Filters
        </button>
        <div className="flex gap-2">
          <button className="p-2 border rounded flex items-center gap-2" onClick={exportToPDF}>
            <BsDownload className="w-4 h-4" /> Export PDF
          </button>
          <button className="p-2 border rounded flex items-center gap-2" onClick={exportToExcel}>
            <BsDownload className="w-4 h-4" /> Export Excel
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : error ? (
        <p className="text-center text-red-500">{error}</p>
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-3 border">Date</th>
                <th className="p-3 border">Time In</th>
                <th className="p-3 border">Time Out</th>
                <th className="p-3 border">Status</th>
                <th className="p-3 border">Total Hours</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? (
                filteredRecords.map((record, index) => (
                  <tr key={record.id || index} className="border-t">
                    <td className="p-3 border">
                      {record.date && !isNaN(new Date(record.date))
                        ? format(new Date(record.date), "MMM d, yyyy")
                        : "-"}
                    </td>
                    <td className="p-3 border">
                      {record.clockInTime && !isNaN(new Date(record.clockInTime))
                        ? format(new Date(record.clockInTime), "hh:mm a")
                        : "-"}
                    </td>
                    <td className="p-3 border">
                      {record.clockOutTime && !isNaN(new Date(record.clockOutTime))
                        ? format(new Date(record.clockOutTime), "hh:mm a")
                        : "-"}
                    </td>
                    <td className={`p-3 border font-semibold ${getStatusClass(record)}`}>
                      {getStatusText(record)}
                    </td>
                    <td className="p-3 border">
                      {record.clockInTime && record.clockOutTime
                        ? formatHoursAndMinutes(
                            (new Date(record.clockOutTime) - new Date(record.clockInTime)) /
                              1000 /
                              60 /
                              60
                          )
                        : "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="p-3 text-center text-gray-500">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-lg font-semibold">
        <span>Total Hours Worked: </span>
        <span>{formatHoursAndMinutes(calculateTotalHours())}</span>
      </div>
    </div>
  );
}
